import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import RecordModal from "../components/RecordModal";
import { toast } from "sonner";
import { Mic, Keyboard, Loader2, Check, ArrowRight, Sparkles, RotateCcw, Feather, Wand2, Eye, PenLine, Lightbulb, BookOpen, Link2 } from "lucide-react";

const LEVELS = [
  { key: "keep_voice", icon: Feather, title: "Keep My Voice", blurb: "A light touch — kept close to the way you actually said it." },
  { key: "polish", icon: Wand2, title: "Polish My Story", blurb: "Tidied and well-written, but still sounds like you.", recommended: true },
  { key: "vivid", icon: Eye, title: "Make It More Vivid", blurb: "A richer, more vivid scene — using only what you told us." },
];

export default function Answer() {
  const navigate = useNavigate();
  const location = useLocation();
  const freeform = !!location.state?.freeform;
  const [question, setQuestion] = useState(location.state?.question || null);
  const [mode, setMode] = useState(location.state?.mode || "type");
  const [transcript, setTranscript] = useState("");
  const [audioId, setAudioId] = useState(null);
  const [rawTranscript, setRawTranscript] = useState(null);
  const [recording, setRecording] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stage, setStage] = useState("input"); // input | followup | ready | story | done
  const [memory, setMemory] = useState(null);
  const [followup, setFollowup] = useState("");
  const [followupAnswer, setFollowupAnswer] = useState("");
  const [followupCount, setFollowupCount] = useState(0);
  const [story, setStory] = useState("");
  const [genStyle, setGenStyle] = useState(null);
  const [tip, setTip] = useState(null);
  const [related, setRelated] = useState(null);
  const [chapterSuggestion, setChapterSuggestion] = useState(null);
  const [addingToChapter, setAddingToChapter] = useState(false);
  const [addedToChapter, setAddedToChapter] = useState(false);

  useEffect(() => {
    if (!question && !freeform) {
      api.get("/questions/today").then(({ data }) => setQuestion(data));
    }
    api.get("/tips/daily").then(({ data }) => setTip(data)).catch(() => {});
    if (mode === "speak") setRecording(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deriveTitle = (text) => {
    const words = text.trim().split(/\s+/);
    const t = words.slice(0, 9).join(" ");
    return (words.length > 9 ? t + "…" : t) || "A memory I wanted to share";
  };

  const onTranscript = (text, aId, raw) => {
    if (stage === "followup") {
      setFollowupAnswer((prev) => (prev ? prev + " " : "") + text);
    } else {
      setTranscript((prev) => (prev ? prev + " " : "") + text);
      setAudioId(aId);
      if (raw) setRawTranscript(raw);
    }
  };

  const save = async () => {
    if (!transcript.trim()) { toast.error("Please share a little before saving."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/memories", {
        question_id: freeform ? null : (question?.id || null),
        question_text: freeform ? deriveTitle(transcript) : (question?.text || "A memory"),
        original_transcript: transcript.trim(),
        raw_transcript: rawTranscript,
        source: audioId ? "voice" : "typed",
        audio_id: audioId,
      });
      setMemory(data.memory);
      setRelated(data.related || null);
      setChapterSuggestion(data.chapter_suggestion || null);
      if (data.followup) { setFollowup(data.followup); setStage("followup"); }
      else { setStage("saved"); }
    } catch {
      toast.error("We couldn't save that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const saveFollowup = async () => {
    if (!followupAnswer.trim()) { setStage("saved"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/memories/${memory.id}/followup-answer`, {
        question: followup, answer: followupAnswer.trim(),
      });
      if (data.memory) setMemory(data.memory);
      const count = followupCount + 1;
      setFollowupCount(count);
      setFollowupAnswer("");
      if (data.next_followup && count < 5) {
        setFollowup(data.next_followup);
      } else {
        setStage("saved");
      }
    } catch {
      toast.error("Couldn't add that, but your memory is saved.");
      setStage("saved");
    } finally {
      setSaving(false);
    }
  };

  const generateStory = async (style) => {
    setGenStyle(style);
    try {
      const { data } = await api.post(`/memories/${memory.id}/generate-memoir`, { style });
      setStory(data.memoir_draft || "");
      setStage("story");
    } catch {
      toast.error("We couldn't write that just now. Please try again.");
    } finally {
      setGenStyle(null);
    }
  };

  const addToChapter = async () => {
    if (!chapterSuggestion || !memory) return;
    setAddingToChapter(true);
    try {
      await api.post(`/chapters/${chapterSuggestion.chapter_id}/add-memory`, { memory_id: memory.id });
      toast.success("Added to your chapter — your earlier writing was kept exactly as it was.");
      setAddedToChapter(true);
    } catch {
      toast.error("We couldn't add that just now. Please try again.");
    } finally {
      setAddingToChapter(false);
    }
  };

  const askAnother = async () => {
    const { data } = await api.get("/questions/next", { params: { exclude: question?.id || "" } });
    setQuestion(data); setTranscript(""); setAudioId(null); setRawTranscript(null); setMemory(null);
    setFollowup(""); setFollowupAnswer(""); setFollowupCount(0); setStory(""); setStage("input"); setMode("type");
    setRelated(null); setChapterSuggestion(null); setAddedToChapter(false);
  };

  return (
    <div className="min-h-screen grain bg-cream relative">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 relative z-10">
        <button data-testid="answer-back-btn" onClick={() => navigate("/home")} className="text-subtle hover:text-charcoal text-sm mb-6">← Home</button>

        {stage === "input" && (
          <div className="animate-fade-up">
            <p className="text-xs uppercase tracking-widest text-sage font-semibold">{freeform ? "In your own words" : "The question"}</p>
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal mt-2" data-testid="answer-question-text">{freeform ? "Tell me a memory" : question?.text}</h1>
            <p className="text-subtle mt-2">
              {freeform
                ? "A moment, a person, a place — anything you'd like to keep. Just tell us what happened; we'll help you find the story in it."
                : "Just tell us what happened, as if you were talking to a friend. Don't worry about getting the words right."}
            </p>

            {tip && (
              <div data-testid="juliet-tip" className="mt-5 flex items-start gap-3 bg-cream2 border border-line rounded-2xl p-4">
                <span className="w-9 h-9 rounded-xl bg-coral/10 text-coral flex items-center justify-center shrink-0"><Lightbulb size={17} /></span>
                <p className="text-sm text-charcoal/80 leading-relaxed"><b className="text-teal">Juliet's tip:</b> {tip.body}</p>
              </div>
            )}

            <div className="mt-6 flex gap-2">
              <button data-testid="mode-speak-btn" onClick={() => { setMode("speak"); setRecording(true); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${mode === "speak" ? "bg-teal text-cream" : "bg-white border border-line text-subtle"}`}>
                <Mic size={16} /> Speak
              </button>
              <button data-testid="mode-type-btn" onClick={() => setMode("type")}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${mode === "type" ? "bg-teal text-cream" : "bg-white border border-line text-subtle"}`}>
                <Keyboard size={16} /> Type
              </button>
            </div>

            {mode === "speak" && !transcript && (
              <button data-testid="start-record-btn" onClick={() => setRecording(true)}
                className="mt-6 w-full flex flex-col items-center gap-3 bg-white border border-line rounded-2xl py-10 hover:border-coral transition-colors">
                <span className="w-16 h-16 rounded-full bg-coral flex items-center justify-center text-white"><Mic size={28} /></span>
                <span className="text-subtle">Tap to start speaking</span>
              </button>
            )}

            <textarea
              data-testid="answer-textarea" value={transcript} onChange={(e) => setTranscript(e.target.value)}
              placeholder={mode === "speak" ? "Your words will appear here, tidied up and ready — you can adjust anything before saving." : "Just start telling the story…"}
              className="mt-5 w-full min-h-[200px] p-5 text-lg leading-relaxed rounded-2xl border border-line bg-white focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none resize-y"
            />
            {mode === "speak" && transcript && (
              <button data-testid="add-more-voice-btn" onClick={() => setRecording(true)} className="mt-3 text-teal text-sm font-medium flex items-center gap-1.5">
                <Mic size={15} /> Add more by voice
              </button>
            )}

            <div className="mt-6 flex items-center justify-between">
              {freeform ? (
                <button data-testid="skip-question-btn" onClick={() => navigate(-1)} className="text-subtle hover:text-charcoal text-sm">Cancel</button>
              ) : (
                <button data-testid="skip-question-btn" onClick={askAnother} className="text-subtle hover:text-charcoal text-sm">Skip this one</button>
              )}
              <button data-testid="save-memory-btn" onClick={save} disabled={saving}
                className="flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-8 h-13 py-3.5 rounded-full font-semibold text-lg transition-colors disabled:opacity-60">
                {saving ? <><Loader2 className="animate-spin" size={20} /> Saving your memory…</> : <>Save & continue <ArrowRight size={20} /></>}
              </button>
            </div>
          </div>
        )}

        {stage === "followup" && (
          <div className="animate-fade-up">
            <div className="flex items-center gap-2 text-sage font-semibold text-sm"><Sparkles size={16} /> Let's go a little deeper</div>
            <div className="mt-4 bg-sagelight rounded-2xl p-6">
              <p className="font-serif text-xl sm:text-2xl text-teal leading-snug" data-testid="followup-question-text">{followup}</p>
            </div>
            <div className="relative">
              <textarea
                data-testid="followup-textarea" value={followupAnswer} onChange={(e) => setFollowupAnswer(e.target.value)} autoFocus
                placeholder="A sentence or two is plenty — type or use the microphone. Or skip if you'd rather not."
                className="mt-4 w-full min-h-[140px] p-5 text-lg leading-relaxed rounded-2xl border border-line bg-white focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none resize-y"
              />
              <button data-testid="followup-voice-btn" onClick={() => setRecording(true)}
                className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-coral hover:bg-coraldark text-white flex items-center justify-center transition-colors shadow-sm" title="Answer by voice">
                <Mic size={18} />
              </button>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <button data-testid="followup-enough-btn" onClick={() => setStage("saved")} className="text-subtle hover:text-charcoal text-sm order-2 sm:order-1">
                I've said enough for now
              </button>
              <button data-testid="followup-save-btn" onClick={saveFollowup} disabled={saving}
                className="order-1 sm:order-2 flex items-center justify-center gap-2 bg-teal hover:bg-tealdark text-cream px-7 py-3 rounded-full font-semibold transition-colors disabled:opacity-60">
                {saving ? <><Loader2 className="animate-spin" size={18} /> Saving…</> : <>Add this & keep going <ArrowRight size={18} /></>}
              </button>
            </div>
          </div>
        )}

        {stage === "saved" && (
          <div className="animate-fade-up text-center py-6">
            <div className="w-16 h-16 rounded-full bg-sagelight flex items-center justify-center mx-auto text-teal"><Check size={32} /></div>
            <h1 className="font-serif text-3xl text-charcoal mt-5">Your memory is safely saved.</h1>
            {memory?.period && (
              <p data-testid="placement-note" className="mt-2 text-subtle max-w-md mx-auto">
                We've placed this with your {memory.period.toLowerCase()} memories. You can change that whenever you like.
              </p>
            )}

            {chapterSuggestion && (
              <div data-testid="chapter-suggestion" className="mt-6 max-w-md mx-auto text-left bg-white border border-line rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-xl bg-teal/10 text-teal flex items-center justify-center shrink-0"><BookOpen size={19} /></span>
                  <div>
                    <p className="text-charcoal leading-relaxed">
                      This sounds as though it belongs with your <b className="text-teal">{chapterSuggestion.title}</b> chapter.
                      {addedToChapter ? " It's been added there." : " Would you like me to add it in?"}
                    </p>
                    {!addedToChapter && (
                      <p className="text-xs text-subtle mt-1">Your earlier writing in that chapter is kept exactly as it is.</p>
                    )}
                  </div>
                </div>
                {addedToChapter ? (
                  <button data-testid="view-chapter-btn" onClick={() => navigate(`/story/${chapterSuggestion.chapter_id}`)}
                    className="mt-4 inline-flex items-center gap-2 text-teal font-semibold text-sm">
                    Read the chapter <ArrowRight size={16} />
                  </button>
                ) : (
                  <button data-testid="add-to-chapter-btn" onClick={addToChapter} disabled={addingToChapter}
                    className="mt-4 inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-5 py-2.5 rounded-full font-semibold text-sm transition-colors disabled:opacity-60">
                    {addingToChapter ? <><Loader2 className="animate-spin" size={16} /> Adding it in…</> : <>Add to this chapter <ArrowRight size={16} /></>}
                  </button>
                )}
              </div>
            )}

            {!chapterSuggestion && related && (
              <div data-testid="related-note" className="mt-6 max-w-md mx-auto text-left bg-cream2 border border-line rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-xl bg-sage/15 text-sage flex items-center justify-center shrink-0"><Link2 size={19} /></span>
                  <div>
                    <p className="text-charcoal leading-relaxed">This seems to connect with something you told me before — I've linked them so they'll sit together in your book.</p>
                    <button data-testid="view-related-btn" onClick={() => navigate(`/memories/${related.memory_id}`)}
                      className="mt-2 inline-flex items-center gap-1.5 text-teal font-semibold text-sm">
                      See the earlier memory <ArrowRight size={15} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <p className="mt-4 text-subtle max-w-md mx-auto">What would you like to do next?</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <button data-testid="see-how-reads-btn" onClick={() => setStage("ready")}
                className="flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-8 h-13 py-3.5 rounded-full font-semibold text-lg transition-colors">
                <Feather size={18} /> See how it reads
              </button>
              <button data-testid="answer-another-btn" onClick={askAnother}
                className="flex items-center justify-center gap-2 bg-white border border-line text-charcoal px-8 py-3.5 rounded-full font-semibold text-lg hover:border-sage transition-colors">
                <RotateCcw size={18} /> Answer another question
              </button>
            </div>
            <button data-testid="saved-view-memory-btn" onClick={() => navigate(`/memories/${memory.id}`)} className="mt-6 text-teal text-sm underline">View this memory</button>
          </div>
        )}

        {stage === "ready" && (
          <div className="animate-fade-up">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-sagelight flex items-center justify-center mx-auto text-teal"><Check size={32} /></div>
              <h1 className="font-serif text-3xl text-charcoal mt-5">Ready to turn this into your story?</h1>
              <p className="mt-3 text-subtle max-w-md mx-auto">We'll write a memoir passage from what you told us — always in your own words, never invented. Choose how much help you'd like.</p>
            </div>
            <div className="mt-8 space-y-3">
              {LEVELS.map((lv) => {
                const Icon = lv.icon;
                const busy = genStyle === lv.key;
                return (
                  <button key={lv.key} data-testid={`story-level-${lv.key}`} onClick={() => generateStory(lv.key)} disabled={!!genStyle}
                    className="w-full text-left flex items-center gap-4 bg-white border border-line hover:border-teal rounded-2xl p-5 transition-colors disabled:opacity-60">
                    <span className="w-12 h-12 rounded-2xl bg-teal/10 text-teal flex items-center justify-center shrink-0">
                      {busy ? <Loader2 className="animate-spin" size={22} /> : <Icon size={22} />}
                    </span>
                    <span className="flex-1">
                      <span className="font-serif text-xl text-charcoal flex items-center gap-2">
                        {lv.title}
                        {lv.recommended && <span className="text-[10px] uppercase tracking-wide font-semibold text-coral bg-coral/10 px-2 py-0.5 rounded-full">Suggested</span>}
                      </span>
                      <span className="block text-subtle text-sm mt-0.5">{lv.blurb}</span>
                    </span>
                    <ArrowRight size={18} className="text-sage shrink-0" />
                  </button>
                );
              })}
            </div>
            {genStyle && (
              <p data-testid="story-generating-note" className="mt-4 text-center text-sm text-teal flex items-center justify-center gap-2">
                <Loader2 className="animate-spin" size={15} /> Writing your story from your own words… your original is always safe.
              </p>
            )}
            <div className="mt-6 text-center">
              <button data-testid="ready-save-only-btn" onClick={() => setStage("done")} className="text-subtle hover:text-charcoal text-sm underline">
                Just save the memory for now
              </button>
            </div>
          </div>
        )}

        {stage === "story" && (
          <div className="animate-fade-up">
            <div className="flex items-center gap-2 text-sage font-semibold text-sm"><Feather size={16} /> Your story, in your words</div>
            <article data-testid="story-passage" className="mt-4 bg-white border border-line rounded-2xl p-6 sm:p-8">
              {story.split(/\n\n+/).filter(Boolean).map((p, i) => (
                <p key={i} className="font-serif text-lg text-charcoal/90 leading-relaxed mb-4 last:mb-0">{p}</p>
              ))}
            </article>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button data-testid="story-keep-btn" onClick={() => setStage("done")}
                className="flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-7 h-13 py-3.5 rounded-full font-semibold text-lg transition-colors">
                <Check size={20} /> Keep this
              </button>
              <button data-testid="story-retry-btn" onClick={() => setStage("ready")}
                className="flex items-center justify-center gap-2 bg-white border border-line text-charcoal px-7 py-3.5 rounded-full font-semibold hover:border-sage transition-colors">
                <RotateCcw size={18} /> Try a different way
              </button>
              <button data-testid="story-edit-btn" onClick={() => navigate(`/memories/${memory.id}`)}
                className="flex items-center justify-center gap-2 text-teal font-semibold px-4 py-3.5">
                <PenLine size={18} /> Edit in full
              </button>
            </div>
          </div>
        )}

        {stage === "done" && (
          <div className="animate-fade-up text-center py-8">
            <div className="w-20 h-20 rounded-full bg-sagelight flex items-center justify-center mx-auto text-teal"><Check size={40} /></div>
            <h1 className="font-serif text-3xl text-charcoal mt-6">{story ? "Story saved" : "Memory saved"}</h1>
            <p className="mt-3 text-subtle">It's now part of your book. We've noted the people and places you mentioned.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <button data-testid="ask-another-btn" onClick={askAnother}
                className="flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-8 h-13 py-3.5 rounded-full font-semibold text-lg transition-colors">
                <RotateCcw size={18} /> Tell me another
              </button>
              <button data-testid="done-today-btn" onClick={() => navigate("/home")}
                className="flex items-center justify-center gap-2 bg-white border border-line text-charcoal px-8 py-3.5 rounded-full font-semibold text-lg hover:border-sage transition-colors">
                I'm done for today
              </button>
            </div>
            <button data-testid="view-memory-btn" onClick={() => navigate(`/memories/${memory.id}`)} className="mt-6 text-teal text-sm underline">View this memory</button>
          </div>
        )}
      </div>

      <RecordModal open={recording} onClose={() => setRecording(false)} onTranscript={onTranscript} />
    </div>
  );
}
